import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import CI from "@trackcode/ci";
import { Icon, StandardButton } from "@trackcode/ui";
import logoTrackCode from "asset/image/trackcode-logo.png";
import * as authAction from "state/auth/authAction";
import { getLogisticCompanySwitcherState } from "./logisticCompanySwitcherUtils";
import {
  LogisticCompanyDropdownInner,
  LogisticCompanyDropdownPanel,
  LogisticCompanyMenuGrid,
  LogisticCompanyMenuGridItem
} from "./logisticCompanyMenuPanel";
import {
  PLATFORM_HEADER_GRADIENT,
  PLATFORM_HEADER_MENU_OPEN_BG,
  PLATFORM_HEADER_MENU_OPEN_SHADOW
} from "./platformHeaderConstants";
import { OnDarkButton } from "./platformHeaderStyles";

class LogisticCompanySwitcher extends Component {
  static propTypes = {
    logisticCompanyId: PropTypes.number.isRequired,
    user: PropTypes.object,
    managedLogisticCompanies: PropTypes.object.isRequired,
    changeLogisticCompanyAction: PropTypes.func.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired
    }).isRequired,
    onAfterSwitch: PropTypes.func,
    variant: PropTypes.oneOf(["default", "onDark"]),
    hideLabel: PropTypes.bool,
    menuLayout: PropTypes.oneOf(["inline", "appBar"]),
    contentInset: PropTypes.number,
    children: PropTypes.node,
    // Optional content rendered next to the burger button but outside of it,
    // so it is not part of the hover-highlighted control (e.g. "× Partner").
    labelSuffix: PropTypes.node
  };

  static defaultProps = {
    user: null,
    onAfterSwitch: null,
    variant: "default",
    hideLabel: false,
    menuLayout: "inline",
    contentInset: CI.space.sm,
    children: null,
    labelSuffix: null
  };

  state = {
    showMenu: false
  };

  componentDidMount() {
    document.addEventListener("mousedown", this.pointerDownListener);
    document.addEventListener("touchstart", this.pointerDownListener);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.pointerDownListener);
    document.removeEventListener("touchstart", this.pointerDownListener);
  }

  setSwitcherRef = ref => {
    this.switcherRef = ref;
  };

  pointerDownListener = event => {
    const { showMenu } = this.state;

    if (!showMenu) {
      return;
    }

    const isInside = this.switcherRef
      ? this.switcherRef.contains(event.target)
      : false;

    if (!isInside) {
      this.setState({ showMenu: false });
    }
  };

  toggleMenu = event => {
    if (event) {
      event.stopPropagation();
    }

    this.setState(prevState => ({ showMenu: !prevState.showMenu }));
  };

  changeLogisticCompany = id => () => {
    const {
      logisticCompanyId,
      changeLogisticCompanyAction,
      history,
      onAfterSwitch
    } = this.props;

    if (id !== logisticCompanyId) {
      changeLogisticCompanyAction(id);

      if (onAfterSwitch) {
        onAfterSwitch(id, history);
      }
    }

    this.setState({ showMenu: false });
  };

  renderControls = ({ isOnDark, hideLabel, currentLabel, labelNode = null }) => {
    const { showMenu } = this.state;
    const useLightControls = isOnDark && showMenu;
    const OpenButton = isOnDark && !showMenu ? OnDarkButton : StandardButton;
    const CloseButton = useLightControls ? StandardButton : OpenButton;
    const hasLabel = labelNode != null || !hideLabel;
    const openLabel =
      labelNode != null ? labelNode : hideLabel ? "" : currentLabel;

    return (
      <Controls>
        <CloseButton
          key="menu-close"
          appearance="subtle"
          style={{ display: showMenu ? "block" : "none" }}
          onClick={this.toggleMenu}
        >
          <Icon name="close" size="md" />
        </CloseButton>

        <OpenButton
          key="menu-open"
          appearance="subtle"
          spaceAfter={false}
          iconBefore={{
            name: "menu",
            width: "21",
            height: "21"
          }}
          textWrap="ellipsis"
          style={{
            display: showMenu ? "none" : "block",
            maxWidth: hasLabel ? "100%" : "none",
            fontSize: "15px",
            fontWeight: "700",
            // Without a label (icon-only burger) drop the trailing padding so
            // the button stays a tight square around the icon.
            ...(hasLabel ? {} : { paddingRight: 0 })
          }}
          onClick={this.toggleMenu}
        >
          {openLabel}
        </OpenButton>
      </Controls>
    );
  };

  renderMenu = ({
    activeLogisticCompanyId,
    menuItems,
    resolveMenuItemLabel
  }) => {
    const { menuLayout, variant } = this.props;
    const { showMenu } = this.state;
    const isOnDark = variant === "onDark";

    const menuContent = (
      <LogisticCompanyMenuGrid>
        {menuItems.map(({ id, name }) => (
          <LogisticCompanyMenuGridItem key={id}>
            <StandardButton
              appearance="subtle"
              shouldFitContainer
              justifyContent="flex-start"
              textWrap="ellipsis"
              style={{
                color:
                  activeLogisticCompanyId === id
                    ? CI.color.brand
                    : CI.color.grayDark,
                fontWeight: activeLogisticCompanyId === id ? "bold" : "normal"
              }}
              onClick={this.changeLogisticCompany(id)}
            >
              {resolveMenuItemLabel(id, name)}
            </StandardButton>
          </LogisticCompanyMenuGridItem>
        ))}
      </LogisticCompanyMenuGrid>
    );

    if (menuLayout === "appBar") {
      return (
        <LogisticCompanyDropdownPanel
          $open={showMenu}
          $embedded={showMenu && isOnDark}
        >
          <LogisticCompanyDropdownInner>{menuContent}</LogisticCompanyDropdownInner>
        </LogisticCompanyDropdownPanel>
      );
    }

    if (!showMenu) {
      return null;
    }

    return <InlineMenu>{menuContent}</InlineMenu>;
  };

  render() {
    const {
      logisticCompanyId,
      user,
      managedLogisticCompanies,
      variant,
      hideLabel,
      menuLayout,
      contentInset,
      children,
      labelSuffix
    } = this.props;
    const { showMenu } = this.state;
    const isOnDark = variant === "onDark";
    const switcherState = getLogisticCompanySwitcherState({
      logisticCompanyId,
      user,
      managedLogisticCompanies
    });

    if (!switcherState.showSwitcher) {
      if (menuLayout === "appBar" && children) {
        return (
          <AppBarLayout>
            <AppBarBar $onDark={isOnDark} $inset={contentInset}>
              <AppBarRow>
                <HeaderContent>
                  {children}
                  {labelSuffix ? (
                    <StaticSuffixSlot>{labelSuffix}</StaticSuffixSlot>
                  ) : null}
                </HeaderContent>
              </AppBarRow>
            </AppBarBar>
          </AppBarLayout>
        );
      }

      return children || null;
    }

    // In the collaboration AppBar the title is passed as `children` while the
    // switcher hides its own text label. Render that title as the burger
    // button's label so the icon + name form a single hoverable control,
    // exactly like the disposition AppBar.
    const labelInsideButton =
      menuLayout === "appBar" && hideLabel && Boolean(children);
    const controls = this.renderControls({
      isOnDark,
      hideLabel,
      currentLabel: switcherState.currentLabel,
      labelNode: labelInsideButton ? children : null
    });
    const menu = this.renderMenu(switcherState);
    const menuOpenOnDark = menuLayout === "appBar" && isOnDark && showMenu;

    if (menuLayout === "appBar") {
      return (
        <AppBarLayout $menuOpen={menuOpenOnDark}>
          <SwitcherRoot ref={this.setSwitcherRef} $menuOpen={showMenu}>
            <AppBarBar $onDark={isOnDark && !showMenu}>
              <AppBarRow
                $compactAdjacentText={hideLabel && Boolean(children)}
              >
                <ControlsSlot>{controls}</ControlsSlot>
                {menuOpenOnDark ? (
                  <OpenMenuCenter>
                    <img src={logoTrackCode} alt="TrackCode" />
                  </OpenMenuCenter>
                ) : labelInsideButton ? (
                  labelSuffix ? (
                    <HeaderContent>{labelSuffix}</HeaderContent>
                  ) : null
                ) : (
                  <HeaderContent>{children}</HeaderContent>
                )}
              </AppBarRow>
            </AppBarBar>
            {menu}
          </SwitcherRoot>
        </AppBarLayout>
      );
    }

    return (
      <InlineRoot ref={this.setSwitcherRef} $onDark={isOnDark}>
        {controls}
        {menu}
      </InlineRoot>
    );
  }
}

const mapStateToProps = ({
  auth: { user, logisticCompanyId = 0, managedLogisticCompanies = {} }
}) => ({
  user,
  logisticCompanyId,
  managedLogisticCompanies
});

const mapDispatchToProps = {
  changeLogisticCompanyAction: authAction.changeLogisticCompany
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LogisticCompanySwitcher)
);

const Controls = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const AppBarLayout = styled.div`
  width: 100%;
  flex-shrink: 0;
  position: relative;
  z-index: 20;

  ${({ $menuOpen }) =>
    $menuOpen
      ? `
    background-image: ${PLATFORM_HEADER_MENU_OPEN_BG};
    box-shadow: ${PLATFORM_HEADER_MENU_OPEN_SHADOW};
  `
      : ""}
`;

const SwitcherRoot = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  z-index: ${({ $menuOpen }) => ($menuOpen ? 21 : "auto")};
`;

const AppBarBar = styled.div`
  background: ${({ $onDark }) =>
    $onDark ? PLATFORM_HEADER_GRADIENT : "transparent"};
  padding: 0 ${CI.space.sm}px 0
    ${({ $inset }) => (typeof $inset === "number" ? $inset : CI.space.sm)}px;
  min-height: 50px;
  display: flex;
  align-items: center;
`;

const AppBarRow = styled.div`
  display: flex;
  align-items: center;
  /* In compact mode the burger button already provides the 6px icon-to-text
     gap via its iconBefore wrapper, so no extra row gap is needed. */
  gap: ${({ $compactAdjacentText }) => ($compactAdjacentText ? "0px" : "16px")};
  min-width: 0;
  width: 100%;
`;

const ControlsSlot = styled.div`
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

// The shared label suffix ("× Partner") carries a -4px left margin that
// compensates for the burger button's trailing padding in the switcher
// layout. Without a switcher there is no button, so the suffix would otherwise
// stick to the customer name. Add the missing spacing back here.
const StaticSuffixSlot = styled.span`
  display: inline-flex;
  align-items: baseline;
  margin-left: 12px;
`;

const OpenMenuCenter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 0;

  img {
    height: 32px;
    padding: 5px 10px;
  }
`;

const InlineRoot = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 16px;
  flex-shrink: 0;

  button {
    color: ${({ $onDark }) => ($onDark ? "#fff" : "inherit")};
  }
`;

const InlineMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 30;
  padding: 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
`;
